import { motion } from 'framer-motion'
import Button from '../components/Button'

const SpaceDetail = () => {
  const space = {
    name: 'Prana Collective',
    tagline: 'A sanctuary for mindful movement and inner exploration',
    location: 'Bangalore – Indiranagar',
    address: '123, MG Road, Indiranagar, Bangalore 560038',
    rating: 4.8,
    reviews: 120,
    founded: 2019,
    facilitatorCount: 12,
    classesPerWeek: 25,
    about: `At Prana Collective, we believe in movement as a medium for healing and connection. Our light-filled studio in Indiranagar hosts daily yoga, sound, and meditation classes led by experienced facilitators. Designed with intention, every corner invites calm — from our airy practice room to our herbal tea bar and reading nook.

The space is home to weekly community circles, seasonal retreats, and teacher trainings that deepen practice and build community.`,
    features: [
      {
        icon: '🌿',
        title: 'Indoor & Outdoor Practice Areas',
        description: 'Spacious halls and serene garden spaces for movement'
      },
      {
        icon: '🔔',
        title: 'Sound & Meditation Studio',
        description: 'Dedicated space for sound healing and deep meditation'
      },
      {
        icon: '☕',
        title: 'Tea Bar & Community Lounge',
        description: 'Connect over herbal teas and meaningful conversations'
      },
      {
        icon: '🪷',
        title: 'Workshops & Retreats',
        description: 'Seasonal programs and immersive experiences'
      },
      {
        icon: '💬',
        title: 'Private Consultations & Healing Rooms',
        description: 'Intimate spaces for one-on-one sessions'
      },
      {
        icon: '🧘',
        title: 'Certified Teacher Trainings',
        description: '200-Hr and 300-Hr YTTC programs'
      }
    ],
    schedule: [
      {
        time: '7:00 AM – 8:00 AM',
        name: 'Hatha Flow',
        facilitator: 'Lisha',
        description: 'A grounding start to your morning with strength and alignment-focused movement.'
      },
      {
        time: '9:30 AM – 10:30 AM',
        name: 'Breath & Stillness (Yin Yoga)',
        facilitator: 'Alok',
        description: 'Deep holds and guided breathwork to release and renew.'
      },
      {
        time: '6:30 PM – 7:30 PM',
        name: 'Sound & Movement',
        facilitator: 'Sandraa',
        description: 'Flowing transitions ending with a sound bath for deep relaxation.'
      },
      {
        time: '8:00 PM – 9:00 PM',
        name: 'Evening Vinyasa',
        facilitator: 'Kavya',
        description: 'Dynamic flow to unwind and restore after a long day.'
      }
    ],
    courses: [
      {
        title: '200-Hour Teacher Training',
        period: 'Jan–Mar 2025',
        facilitators: 'Lisha & Rajesh',
        focus: 'Hatha, Ashtanga, and Teaching Methodology',
        description: 'A comprehensive program designed to deepen your practice and develop your teaching skills. Includes philosophy, anatomy, alignment, and hands-on teaching experience.'
      },
      {
        title: 'Healing with Sound',
        period: 'Weekend Workshop',
        facilitators: 'Sandraa',
        focus: 'Sound Healing & Vibration Therapy',
        description: 'Explore the science and art of vibration-based healing. Learn to use singing bowls, gongs, and voice as tools for transformation.'
      },
      {
        title: 'Monthly Community Circles',
        period: 'First Saturday of Each Month',
        facilitators: 'Various',
        focus: 'Peer-led Discussions & Guided Meditations',
        description: 'Join our community for peer-led discussions and guided meditations on wellness topics. Open to all levels.'
      }
    ],
    facilitators: [
      {
        name: 'Lisha',
        specialization: 'Hatha, Yin, Holistic Yoga',
        image: '/lisha.jpg'
      },
      {
        name: 'Sandraa',
        specialization: 'Sound Healing, Breathwork',
        image: '/placeholder-facilitator.jpg'
      },
      {
        name: 'Alok',
        specialization: 'Meditation, Yoga Nidra',
        image: '/placeholder-facilitator.jpg'
      },
      {
        name: 'Kavya',
        specialization: 'Expressive Arts Therapy',
        image: '/placeholder-facilitator.jpg'
      },
      {
        name: 'Rajesh',
        specialization: 'Ashtanga, Vinyasa',
        image: '/placeholder-facilitator.jpg'
      },
      {
        name: 'Meera',
        specialization: 'Restorative Yoga, Ayurveda',
        image: '/placeholder-facilitator.jpg'
      }
    ],
    gallery: [
      '/placeholder-space-1.jpg',
      '/placeholder-space-2.jpg',
      '/placeholder-space-3.jpg',
      '/placeholder-space-4.jpg',
      '/placeholder-space-5.jpg',
      '/placeholder-space-6.jpg'
    ],
    testimonials: [
      {
        text: 'Prana Collective feels like home — the teachers here truly care about your journey. The space itself is so welcoming and peaceful.',
        author: 'Raghav M.',
        role: 'Student'
      },
      {
        text: 'A beautiful balance of energy and calmness. Every class leaves me feeling more grounded and connected to myself.',
        author: 'Chetna S.',
        role: 'Student'
      },
      {
        text: 'Teaching at Prana Collective has been transformative. The community here is supportive, and the space allows for authentic sharing.',
        author: 'Lisha',
        role: 'Facilitator'
      }
    ],
    contact: {
      phone: '+91 98765 43210',
      email: 'hello@pranacollective.in',
      instagram: '@pranacollective',
      whatsapp: '+91 98765 43210'
    }
  }

  return (
    <div className="bg-cream min-h-screen">
      {/* Header Section with Cover Image */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="relative bg-gradient-to-b from-cream via-cream-50 to-cream pt-20 pb-20"
      >
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Cover Image */}
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.5 }}
              className="relative w-full h-64 md:h-80 rounded-3xl overflow-hidden shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15)] ring-1 ring-white/50 mb-8"
            >
              <img
                src="/yogastudio.jpg"
                alt="Prana Collective Studio"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-white/20 via-transparent to-transparent"></div>
            </motion.div>

            {/* Space Info */}
            <div className="flex flex-col md:flex-row items-center md:items-start gap-12">
              {/* Main Info */}
              <div className="flex-1 text-center md:text-left">
                <motion.h1
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="text-4xl md:text-5xl font-bold text-gray-900 mb-3 tracking-tight"
                >
                  {space.name}
                </motion.h1>
                <motion.p
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-xl md:text-2xl text-primary-600 font-medium mb-6 tracking-wide italic"
                >
                  {space.tagline}
                </motion.p>

                {/* Location */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="flex items-center gap-3 mb-6 justify-center md:justify-start"
                >
                  <div className="p-2 rounded-full bg-primary-50">
                    <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <span className="font-medium text-lg text-gray-700">{space.location}</span>
                </motion.div>

                {/* Stats Grid */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
                >
                  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-sm border border-white/50">
                    <div className="text-2xl font-bold text-primary-600">{space.rating}</div>
                    <div className="text-sm text-gray-600">⭐ Rating</div>
                    <div className="text-xs text-gray-500 mt-1">({space.reviews} reviews)</div>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-sm border border-white/50">
                    <div className="text-2xl font-bold text-primary-600">{space.founded}</div>
                    <div className="text-sm text-gray-600">Founded</div>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-sm border border-white/50">
                    <div className="text-2xl font-bold text-primary-600">{space.facilitatorCount}</div>
                    <div className="text-sm text-gray-600">Facilitators</div>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-sm border border-white/50">
                    <div className="text-2xl font-bold text-primary-600">{space.classesPerWeek}</div>
                    <div className="text-sm text-gray-600">Classes/week</div>
                  </div>
                </motion.div>

                {/* CTA Buttons */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start"
                >
                  <Button
                    variant="primary"
                    onClick={() => console.log('View Schedule clicked')}
                    className="px-8 py-4 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    View Schedule
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => console.log('Book a Class clicked')}
                    className="px-8 py-4 text-lg rounded-xl border-2 hover:bg-primary-50 transition-all duration-300"
                  >
                    Book a Class
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => console.log('Visit the Space clicked')}
                    className="px-8 py-4 text-lg rounded-xl border-2 hover:bg-primary-50 transition-all duration-300"
                  >
                    Visit the Space
                  </Button>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto space-y-16">
          {/* About Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="relative"
          >
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-10 md:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-white/50">
              <h2 className="text-4xl md:text-4xl font-bold text-gray-900 mb-8 tracking-tight">About the Space</h2>
              <p className="text-lg md:text-xl text-gray-700/90 leading-relaxed whitespace-pre-line">
                {space.about}
              </p>
            </div>
          </motion.div>

          {/* Features Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="relative"
          >
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-10 md:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-white/50">
              <h2 className="text-4xl md:text-4xl font-bold text-gray-900 mb-10 tracking-tight">What Makes Us Special</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {space.features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    className="group relative bg-gradient-to-br from-white to-gray-50/50 rounded-2xl p-6 shadow-sm hover:shadow-[0_12px_40px_rgb(0,0,0,0.1)] transition-all duration-300 border border-gray-100/50 hover:border-primary-200/50"
                  >
                    <div className="text-4xl mb-4">{feature.icon}</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Class Schedule Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="relative"
          >
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-10 md:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-white/50">
              <div className="flex items-center justify-between mb-10">
                <h2 className="text-4xl md:text-4xl font-bold text-gray-900 tracking-tight">Class Schedule</h2>
                <Button
                  variant="outline"
                  onClick={() => console.log('View Full Schedule clicked')}
                  className="px-6 py-3 rounded-xl"
                >
                  View Full Schedule
                </Button>
              </div>
              <div className="space-y-6">
                {space.schedule.map((classItem, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    className="group relative bg-gradient-to-br from-white to-gray-50/50 rounded-2xl p-6 md:p-8 shadow-sm hover:shadow-[0_12px_40px_rgb(0,0,0,0.1)] transition-all duration-300 border border-gray-100/50 hover:border-primary-200/50"
                  >
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                      <div className="flex-1 space-y-4">
                        <div className="flex items-start gap-4">
                          <div className="p-3 rounded-xl bg-primary-100/50 group-hover:bg-primary-100 transition-colors shrink-0">
                            <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-primary-600 mb-1 uppercase tracking-wide">
                              {classItem.time}
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                              {classItem.name}
                            </h3>
                            <p className="text-gray-500 text-sm mb-2">
                              with <span className="font-semibold text-gray-700">{classItem.facilitator}</span>
                            </p>
                            <p className="text-gray-600 leading-relaxed text-lg">
                              {classItem.description}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center md:items-start md:pt-2">
                        <Button
                          variant="primary"
                          onClick={() => console.log(`Book ${classItem.name} clicked`)}
                          className="px-6 py-3 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 whitespace-nowrap"
                        >
                          Book Class
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Courses & Workshops Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="relative"
          >
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-10 md:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-white/50">
              <h2 className="text-4xl md:text-4xl font-bold text-gray-900 mb-10 tracking-tight">Courses & Workshops</h2>
              <div className="space-y-6">
                {space.courses.map((course, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 + index * 0.1 }}
                    className="group relative bg-gradient-to-br from-primary-50/80 via-primary-50/50 to-white rounded-2xl p-8 shadow-sm hover:shadow-[0_12px_40px_rgb(0,0,0,0.12)] transition-all duration-300 border border-primary-100/50 hover:border-primary-200/70 overflow-hidden"
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-primary-200/20 rounded-full -mr-16 -mt-16 blur-2xl"></div>
                    <div className="relative">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900 group-hover:text-primary-700 transition-colors mb-2">
                            {course.title}
                          </h3>
                          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                            <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full font-semibold">
                              {course.period}
                            </span>
                            <span className="text-gray-500">Led by: <span className="font-semibold text-gray-700">{course.facilitators}</span></span>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm font-semibold text-primary-600 mb-3">{course.focus}</p>
                      <p className="text-gray-700 mb-6 text-lg leading-relaxed">{course.description}</p>
                      <Button
                        variant="primary"
                        onClick={() => console.log(`Learn More about ${course.title} clicked`)}
                        className="rounded-xl py-3 px-6 shadow-md hover:shadow-lg transition-all duration-300"
                      >
                        Learn More
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Facilitators Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="relative"
          >
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-10 md:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-white/50">
              <div className="flex items-center justify-between mb-10">
                <h2 className="text-4xl md:text-4xl font-bold text-gray-900 tracking-tight">Facilitators at This Space</h2>
                <Button
                  variant="outline"
                  onClick={() => console.log('Meet All Facilitators clicked')}
                  className="px-6 py-3 rounded-xl"
                >
                  Meet All Facilitators
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {space.facilitators.map((facilitator, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.7 + index * 0.1 }}
                    className="group relative bg-gradient-to-br from-white to-gray-50/50 rounded-2xl p-6 shadow-sm hover:shadow-[0_12px_40px_rgb(0,0,0,0.1)] transition-all duration-300 border border-gray-100/50 hover:border-primary-200/50 text-center cursor-pointer"
                    onClick={() => console.log(`View ${facilitator.name} profile clicked`)}
                  >
                    <div className="relative w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden shadow-md ring-2 ring-white group-hover:ring-primary-200 transition-all">
                      <div className="w-full h-full bg-gradient-to-br from-primary-200 to-primary-400 flex items-center justify-center text-white text-2xl font-bold">
                        {facilitator.name.charAt(0)}
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-primary-600 transition-colors">
                      {facilitator.name}
                    </h3>
                    <p className="text-sm text-gray-600">{facilitator.specialization}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Gallery Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="relative"
          >
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-10 md:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-white/50">
              <h2 className="text-4xl md:text-4xl font-bold text-gray-900 mb-10 tracking-tight">Gallery</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {space.gallery.map((image, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.8 + index * 0.05 }}
                    className="relative aspect-square rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group cursor-pointer"
                  >
                    <div className="w-full h-full bg-gradient-to-br from-primary-200 via-primary-300 to-primary-400 flex items-center justify-center">
                      <span className="text-white text-sm font-semibold">Space Photo {index + 1}</span>
                    </div>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors"></div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Testimonials Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="relative"
          >
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-10 md:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-white/50">
              <h2 className="text-4xl md:text-4xl font-bold text-gray-900 mb-10 tracking-tight">What People Say</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {space.testimonials.map((testimonial, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.9 + index * 0.1 }}
                    className="relative bg-gradient-to-br from-white to-gray-50/50 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100/50"
                  >
                    <div className="text-4xl text-primary-200 mb-4">&ldquo;</div>
                    <p className="text-gray-700 leading-relaxed mb-4 text-lg italic">
                      {testimonial.text}
                    </p>
                    <div className="pt-4 border-t border-gray-200">
                      <p className="font-semibold text-gray-900">{testimonial.author}</p>
                      <p className="text-sm text-gray-500">{testimonial.role}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Contact & Booking Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
            className="relative"
          >
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-10 md:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-white/50">
              <h2 className="text-4xl md:text-4xl font-bold text-gray-900 mb-10 tracking-tight">Visit Us</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Contact Info */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Contact Information</h3>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-full bg-primary-50 shrink-0">
                          <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{space.address}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-full bg-primary-50 shrink-0">
                          <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                        </div>
                        <a href={`tel:${space.contact.phone}`} className="text-gray-700 hover:text-primary-600 transition-colors">
                          {space.contact.phone}
                        </a>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-full bg-primary-50 shrink-0">
                          <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <a href={`mailto:${space.contact.email}`} className="text-gray-700 hover:text-primary-600 transition-colors">
                          {space.contact.email}
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Social Links */}
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Connect With Us</h3>
                    <div className="flex gap-4">
                      <Button
                        variant="outline"
                        onClick={() => console.log('Instagram clicked')}
                        className="px-6 py-3 rounded-xl"
                      >
                        Instagram
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => console.log('WhatsApp clicked')}
                        className="px-6 py-3 rounded-xl"
                      >
                        WhatsApp
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Map Placeholder */}
                <div className="relative rounded-2xl overflow-hidden shadow-md h-full min-h-[300px]">
                  <div className="w-full h-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
                    <div className="text-center">
                      <svg className="w-16 h-16 text-primary-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <p className="text-gray-600 font-medium">Map Integration</p>
                      <p className="text-sm text-gray-500 mt-2">{space.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Booking CTAs */}
              <div className="mt-10 pt-10 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    variant="primary"
                    onClick={() => console.log('Book a Class clicked')}
                    className="px-8 py-4 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    Book a Class
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => console.log('Host Your Workshop Here clicked')}
                    className="px-8 py-4 text-lg rounded-xl border-2 hover:bg-primary-50 transition-all duration-300"
                  >
                    Host Your Workshop Here
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => console.log('Enquire for Retreats clicked')}
                    className="px-8 py-4 text-lg rounded-xl border-2 hover:bg-primary-50 transition-all duration-300"
                  >
                    Enquire for Retreats
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Final CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1 }}
            className="relative"
          >
            <div className="relative bg-gradient-to-br from-primary-600 via-primary-600 to-primary-700 rounded-3xl p-12 md:p-16 shadow-[0_20px_60px_-15px_rgba(56,90,57,0.4)] overflow-hidden mb-8">
              <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -mr-48 -mt-48 blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 rounded-full -ml-48 -mb-48 blur-3xl"></div>
              <div className="relative text-center">
                <h2 className="text-4xl text-primary-50 md:text-4xl font-bold mb-6 tracking-tight">Start Your Journey</h2>
                <p className="text-xl text-primary-50 mb-10 max-w-2xl mx-auto leading-relaxed">
                  Join us at Prana Collective and discover a community dedicated to mindful movement, inner exploration, and authentic connection.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    variant="secondary"
                    onClick={() => console.log('Book Your First Class clicked')}
                    className="px-10 py-4 text-lg rounded-xl bg-white text-primary-600 hover:bg-gray-50 shadow-xl hover:shadow-2xl transition-all duration-300 font-semibold"
                  >
                    Book Your First Class
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => console.log('Schedule a Visit clicked')}
                    className="px-10 py-4 text-lg rounded-xl border-2 border-white text-white hover:bg-white/10 transition-all duration-300 font-semibold"
                  >
                    Schedule a Visit
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default SpaceDetail

